// Bulk-edit sheet for the slots heatmap's detail rail. Opens when the user
// clicks "Edit all" in the rail and lets them change everything the per-slot
// `SlotFormSheet` exposes EXCEPT identity fields (doctor / tenant / calendar /
// anchor date) — those are inherently per-slot and stay untouched.
//
// The sheet is partial: only fields the user actually touched (or changed
// from the shared initial value) are reported as "changed". On Apply the
// parent receives both the form values AND a `changed` map so it can build a
// per-slot PUT body that only updates the touched fields — every other field
// on every slot is left alone.
//
// Initial values pre-fill from the slot set: when every slot agrees on a
// field the shared value seeds it; otherwise the field shows a "Mixed"
// badge and seeds with the first slot's value. Touching a Mixed field is
// always reported as a change (it homogenizes the group); touching a Shared
// field is only reported as a change if the value actually differs from the
// initial.

import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { fetchCalendars } from "@/lib/api/calendar";
import { calendarOptionLabel } from "@/lib/calendars";
import { cn } from "@/lib/utils";

// Same lists the per-slot form uses — keep these in lockstep with
// `slot-form-sheet.jsx` so the pickers look identical.
const WEEKDAY_LABELS = [
  { iso: 1, short: "Mo" },
  { iso: 2, short: "Tu" },
  { iso: 3, short: "We" },
  { iso: 4, short: "Th" },
  { iso: 5, short: "Fr" },
  { iso: 6, short: "Sa" },
  { iso: 7, short: "Su" },
];

const MONTH_WEEK_LABELS = [
  { value: 1, short: "1st" },
  { value: 2, short: "2nd" },
  { value: 3, short: "3rd" },
  { value: 4, short: "4th" },
  { value: -1, short: "Last" },
];

const MONTH_DAY_VALUES = Array.from({ length: 31 }, (_, i) => i + 1);

// Visit subtype pill values — mirror the per-slot form. "" = no restriction.
const SUB_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "firstVisit", label: "First visit" },
  { value: "followUp", label: "Follow-up" },
];

function normalizeSubType(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "firstvisit" || v === "first_visit" || v === "first-visit") return "firstVisit";
  if (v === "followup" || v === "follow_up" || v === "follow-up") return "followUp";
  return "";
}

const COMMON_TIMEZONES = [
  "UTC",
  "Europe/Warsaw",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

// Human-readable label for each form field. Used to render the live "Will
// update: …" hint and to compose the post-save toast in the parent. Exported
// so the page handler can reuse the same labels.
export const BULK_FIELD_LABELS = {
  // tenantId / doctorId / calendarId are duplicate-mode only — they re-target
  // the copies to a different clinic / doctor / calendar. The labels read in
  // plain English to match the rest of the user-facing copy (the data keys
  // stay `tenantId`/`doctorId`/`calendarId`).
  tenantId: "clinic",
  doctorId: "doctor",
  calendarId: "calendar",
  slotType: "type",
  subType: "visit subtype",
  moreTests: "more-tests flag",
  startTime: "start time",
  endTime: "end time",
  timezone: "timezone",
  date: "date",
  frequency: "frequency",
  weekdays: "weekdays",
  monthlyVariant: "monthly pattern",
  monthDays: "days of the month",
  monthWeeks: "weeks of the month",
  repeatIntervalWeeks: "weekly interval",
  repeatIntervalMonths: "monthly interval",
  dates: "specific dates",
  excludeDates: "excluded dates",
  bookingMode: "booking mode",
  placeholder: "label",
  colorHex: "color",
};

// YYYY-MM-DD <-> Date helpers (mirror the per-slot form so the picker UX is
// identical).
function pad2(n) {
  return String(n).padStart(2, "0");
}
function dateToYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
function ymdToDate(ymd) {
  if (typeof ymd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function FieldLabel({ children, mixed }) {
  return (
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <span>{children}</span>
      {mixed ? (
        <span className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
          Mixed
        </span>
      ) : null}
    </p>
  );
}

// Searchable picker used by the duplicate-mode-only identity overrides
// (clinic / doctor / calendar). Mirrors the look of slot-form-sheet.jsx's
// ComboboxField but adds a "Mixed" affordance — the trigger label reads in
// italics when the slots disagree and the user hasn't picked an override
// yet, so the field reads consistently with the other Mixed-aware controls
// in this sheet. Loading + disabled states match the other identity pickers
// (e.g. Calendar is gated on a Doctor pick).
function OverrideComboField({
  label,
  noun,
  value,
  mixed,
  options,
  onChange,
  placeholder,
  disabled = false,
  loading = false,
  error,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((opt) => opt.value === value);
  const showMixed = mixed && !selected;
  const triggerText = selected
    ? selected.label
    : showMixed
      ? `Mixed — pick a ${noun} to override every copy`
      : placeholder;

  return (
    <div className="space-y-1.5">
      <FieldLabel mixed={mixed}>{label}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "h-9 w-full justify-between rounded-md border border-input bg-transparent px-3 text-left text-sm font-normal shadow-sm",
              !selected && "text-muted-foreground",
              showMixed && "italic",
              error && "border-destructive",
            )}
          >
            <span className="truncate">{triggerText}</span>
            {loading ? (
              <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${noun}…`} />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={`${opt.label} ${opt.value}`}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === opt.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function PillButton({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "inline-flex h-8 min-w-[36px] items-center justify-center rounded-md border px-3 text-xs font-semibold transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// Togglable card for the follow-up "More tests needed" flag in bulk-edit.
// Matches the per-slot form's styling. Adds a third visual state when the
// group's slots disagree (Mixed): dashed border + amber accent until the user
// touches the control to homogenize the group.
function MoreTestsToggle({ checked, mixed, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={mixed ? "mixed" : checked}
      onClick={() => onToggle(!checked)}
      className={cn(
        "group mt-1 flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        mixed
          ? "border-dashed border-amber-500/50 bg-amber-500/[0.04] hover:border-amber-500/70"
          : checked
            ? "border-violet-500/60 bg-violet-500/[0.06] shadow-sm"
            : "border-border bg-background hover:border-foreground/20 hover:bg-muted/40",
      )}
    >
      <div
        className={cn(
          "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-[1.5px] transition-colors",
          mixed
            ? "border-amber-500/70 bg-amber-500/15 text-amber-700 dark:text-amber-300"
            : checked
              ? "border-violet-500 bg-violet-500 text-white"
              : "border-border bg-background group-hover:border-foreground/40",
        )}
        aria-hidden="true"
      >
        {mixed ? (
          <span className="block h-[2px] w-2.5 rounded-full bg-current" />
        ) : checked ? (
          <Check className="size-3.5" strokeWidth={3} />
        ) : null}
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">More tests needed</span>
          <span
            className={cn(
              "rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide transition-colors",
              checked && !mixed
                ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "bg-muted text-muted-foreground/70",
            )}
          >
            Follow-up{checked && !mixed ? "+" : ""}
          </span>
          {mixed ? (
            <span className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
              Mixed
            </span>
          ) : null}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {mixed
            ? "Slots in this group disagree. Toggle to set the flag on every slot at once."
            : "Tags every slot in this group as needing additional tests."}
        </p>
      </div>
    </button>
  );
}

// Date-list picker for the bulk-edit's "Also include specific dates" block.
// Mirrors the per-slot form's picker so the UX stays consistent: pick a date
// via `DatePicker`, hit "Add date", removable chips below.
function BulkDateListPicker({ dates, onAdd, onRemove }) {
  const [draft, setDraft] = useState(null);

  const handleAdd = () => {
    if (!(draft instanceof Date) || Number.isNaN(draft.getTime())) return;
    onAdd(draft);
    setDraft(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <DatePicker date={draft} setDate={setDraft} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0 [&_svg]:size-3.5"
          onClick={handleAdd}
          disabled={!(draft instanceof Date)}
        >
          <Plus />
          Add date
        </Button>
      </div>
      {dates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No extra dates yet. Pick a date above and press <em>Add date</em>.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {dates.map((ymd) => (
            <span
              key={ymd}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-mono tabular-nums"
            >
              {ymd}
              <button
                type="button"
                onClick={() => onRemove(ymd)}
                className="inline-flex size-4 items-center justify-center rounded text-muted-foreground/70 transition-colors hover:text-destructive"
                title={`Remove ${ymd}`}
                aria-label={`Remove ${ymd}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Deep-equality for primitive arrays (used to detect when every slot agrees on
// a list field like `weekdays` or `monthDays`).
function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((value, idx) => value === sb[idx]);
}

// Returns the shared value of `getter(slot)` when every slot agrees, otherwise
// `null`. `null` lets the caller distinguish "all agree" from "mixed".
function readShared(slots, getter) {
  if (!Array.isArray(slots) || slots.length === 0) return { shared: null, mixed: false };
  const first = getter(slots[0]);
  const allMatch = slots.every((slot) => {
    const value = getter(slot);
    if (Array.isArray(first) || Array.isArray(value)) return arraysEqual(first, value);
    return value === first;
  });
  return { shared: allMatch ? first : null, mixed: !allMatch };
}

// Sort month-day / month-week values so the "Last" sentinel (-1) lands at the
// end rather than the beginning (mirrors the slot-form-sheet helpers).
function sortMonthSentinelValues(values) {
  return [...values].sort((a, b) => {
    if (a === -1) return 1;
    if (b === -1) return -1;
    return a - b;
  });
}

// Build the initial form state from the slot set. For each field, pre-fill the
// shared value when all slots agree; otherwise pre-fill the first slot's value
// and mark the field "mixed" for the UI.
function deriveInitialForm(slots) {
  const fallback = {
    // Duplicate-only identity overrides. All three start blank so the pickers
    // show "Mixed" / placeholder until the user explicitly chooses a target —
    // we don't want to silently pre-pick anything they didn't authorize. In
    // edit mode these fields aren't rendered and their values are ignored.
    tenantId: "",
    doctorId: "",
    calendarId: "",
    slotType: "recurring",
    subType: "",
    moreTests: false,
    startTime: "09:00",
    endTime: "09:30",
    timezone: "UTC",
    date: "",
    frequency: "weekly",
    weekdays: [],
    monthlyVariant: "dayOfMonth",
    monthDays: [],
    monthWeeks: [],
    repeatIntervalWeeks: 1,
    repeatIntervalMonths: 1,
    dates: [],
    excludeDates: [],
    bookingMode: "auto",
    placeholder: "",
    colorHex: "",
  };
  if (!Array.isArray(slots) || slots.length === 0) {
    return { form: fallback, mixed: {} };
  }

  const sample = slots[0] || {};
  const sampleRecurrence =
    sample.recurrence && typeof sample.recurrence === "object" ? sample.recurrence : {};

  const tenantId = readShared(slots, (slot) => String(slot?.tenantId || ""));
  // doctorId mixed flag is true whenever the group spans multiple doctors —
  // possible when the user groups by tenant and the cell holds slots from
  // several doctors. Calendar mixed is tracked too, though it's strongly
  // coupled to doctor (a calendar is doctor-scoped, so the picker only opens
  // up after a doctor override is chosen).
  const doctorId = readShared(slots, (slot) => String(slot?.doctorId || ""));
  const calendarId = readShared(slots, (slot) => String(slot?.calendarId || ""));
  const slotType = readShared(slots, (slot) =>
    slot?.slotType === "recurring" ? "recurring" : "static",
  );
  const subType = readShared(slots, (slot) => normalizeSubType(slot?.subType));
  const moreTests = readShared(slots, (slot) => slot?.metadata?._moreTests === true);
  const startTime = readShared(slots, (slot) =>
    String(
      slot?.recurrence?.startTime || slot?.startAt?.toFormat?.("HH:mm") || "09:00",
    ),
  );
  const endTime = readShared(slots, (slot) =>
    String(slot?.recurrence?.endTime || slot?.endAt?.toFormat?.("HH:mm") || "09:30"),
  );
  const timezone = readShared(slots, (slot) =>
    String(slot?.timezone || slot?.recurrence?.timezone || "UTC"),
  );
  const frequency = readShared(slots, (slot) =>
    String(slot?.recurrence?.frequency || "weekly").toLowerCase() === "monthly"
      ? "monthly"
      : "weekly",
  );
  const weekdays = readShared(slots, (slot) =>
    Array.isArray(slot?.recurrence?.weekdays)
      ? [...slot.recurrence.weekdays].sort((a, b) => a - b)
      : [],
  );
  const monthDays = readShared(slots, (slot) =>
    Array.isArray(slot?.recurrence?.monthDays)
      ? sortMonthSentinelValues(slot.recurrence.monthDays)
      : [],
  );
  const monthWeeks = readShared(slots, (slot) =>
    Array.isArray(slot?.recurrence?.monthWeeks)
      ? sortMonthSentinelValues(slot.recurrence.monthWeeks)
      : [],
  );
  const repeatIntervalWeeks = readShared(
    slots,
    (slot) => Number(slot?.recurrence?.repeatIntervalWeeks) || 1,
  );
  const repeatIntervalMonths = readShared(
    slots,
    (slot) => Number(slot?.recurrence?.repeatIntervalMonths) || 1,
  );
  const dates = readShared(slots, (slot) =>
    Array.isArray(slot?.recurrence?.dates)
      ? [...slot.recurrence.dates]
          .map((d) => (typeof d === "string" ? d.trim() : ""))
          .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
          .sort()
      : [],
  );
  const excludeDates = readShared(slots, (slot) =>
    Array.isArray(slot?.recurrence?.excludeDates)
      ? [...slot.recurrence.excludeDates]
          .map((d) => (typeof d === "string" ? d.trim() : ""))
          .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
          .sort()
      : [],
  );
  const bookingMode = readShared(slots, (slot) =>
    String(slot?.bookingMode || "auto") === "manual" ? "manual" : "auto",
  );
  const placeholder = readShared(slots, (slot) => String(slot?.placeholder || ""));
  const colorHex = readShared(slots, (slot) =>
    String(slot?.colorHex || slot?.metadata?.colorHex || ""),
  );
  // Per-slot anchor date (YYYY-MM-DD). Only surfaced in the duplicate sheet for
  // static slots so the user can drop the copies onto a specific date.
  const anchorDate = readShared(slots, (slot) => slot?.startAt?.toISODate?.() || "");

  // monthlyVariant: pick whichever array the sample slot populated; fall back
  // to dayOfMonth when neither array carries data.
  const sampleMonthDays = Array.isArray(sampleRecurrence.monthDays)
    ? sampleRecurrence.monthDays
    : [];
  const sampleMonthWeeks = Array.isArray(sampleRecurrence.monthWeeks)
    ? sampleRecurrence.monthWeeks
    : [];
  let monthlyVariant = "dayOfMonth";
  if (sampleMonthWeeks.length > 0 && sampleMonthDays.length === 0) {
    monthlyVariant = "nthWeekday";
  }

  const form = {
    // tenantId / doctorId / calendarId start blank so the duplicate pickers
    // show "Mixed" / placeholder until the user explicitly chooses one. Each
    // pick flips the field's `changed` flag and re-targets every copy.
    tenantId: fallback.tenantId,
    doctorId: fallback.doctorId,
    calendarId: fallback.calendarId,
    slotType: slotType.shared || sample.slotType || fallback.slotType,
    subType: subType.shared ?? normalizeSubType(sample.subType),
    moreTests: moreTests.shared ?? sample?.metadata?._moreTests === true,
    startTime: startTime.shared || fallback.startTime,
    endTime: endTime.shared || fallback.endTime,
    timezone: timezone.shared || fallback.timezone,
    date: anchorDate.shared || fallback.date,
    frequency: frequency.shared || fallback.frequency,
    weekdays: Array.isArray(weekdays.shared)
      ? weekdays.shared
      : Array.isArray(sampleRecurrence.weekdays)
        ? sampleRecurrence.weekdays
        : [],
    monthlyVariant,
    monthDays: Array.isArray(monthDays.shared)
      ? monthDays.shared
      : sortMonthSentinelValues(sampleMonthDays),
    monthWeeks: Array.isArray(monthWeeks.shared)
      ? monthWeeks.shared
      : sortMonthSentinelValues(sampleMonthWeeks),
    repeatIntervalWeeks: repeatIntervalWeeks.shared ?? fallback.repeatIntervalWeeks,
    repeatIntervalMonths: repeatIntervalMonths.shared ?? fallback.repeatIntervalMonths,
    dates: Array.isArray(dates.shared)
      ? dates.shared
      : Array.isArray(sampleRecurrence.dates)
        ? [...sampleRecurrence.dates]
            .map((d) => (typeof d === "string" ? d.trim() : ""))
            .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
            .sort()
        : [],
    excludeDates: Array.isArray(excludeDates.shared)
      ? excludeDates.shared
      : Array.isArray(sampleRecurrence.excludeDates)
        ? [...sampleRecurrence.excludeDates]
            .map((d) => (typeof d === "string" ? d.trim() : ""))
            .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
            .sort()
        : [],
    bookingMode: bookingMode.shared || fallback.bookingMode,
    placeholder: placeholder.shared ?? fallback.placeholder,
    colorHex: colorHex.shared ?? fallback.colorHex,
  };

  const mixed = {
    // tenantId.mixed is true whenever the slot group spans multiple clinics
    // (only possible when grouped by doctor); the picker uses it to render a
    // "Mixed" affordance. doctorId / calendarId follow the same pattern —
    // doctor mixed is only possible when grouped by tenant; calendar mixed
    // is common even within one doctor.
    tenantId: tenantId.mixed,
    doctorId: doctorId.mixed,
    calendarId: calendarId.mixed,
    slotType: slotType.mixed,
    subType: subType.mixed,
    moreTests: moreTests.mixed,
    startTime: startTime.mixed,
    endTime: endTime.mixed,
    timezone: timezone.mixed,
    date: anchorDate.mixed,
    frequency: frequency.mixed,
    weekdays: weekdays.mixed,
    monthDays: monthDays.mixed,
    monthWeeks: monthWeeks.mixed,
    repeatIntervalWeeks: repeatIntervalWeeks.mixed,
    repeatIntervalMonths: repeatIntervalMonths.mixed,
    dates: dates.mixed,
    excludeDates: excludeDates.mixed,
    bookingMode: bookingMode.mixed,
    placeholder: placeholder.mixed,
    colorHex: colorHex.mixed,
  };

  return { form, mixed };
}

// `mode`:
//   • "edit"      — Apply mutates the existing slots in place (default).
//   • "duplicate" — Apply creates new slots cloned from each source with the
//                   touched fields overridden. Header / button copy switches
//                   and Apply stays enabled even with zero edits (creating
//                   exact copies is a valid action).
export function BulkEditSheet({
  open,
  onOpenChange,
  slots = [],
  busy = false,
  onApply,
  mode = "edit",
  // Duplicate-mode only — populate the identity-override pickers (Clinic,
  // Doctor, Calendar). Doctor is followed by a Calendar picker because
  // calendars are doctor-scoped; we lazily fetch them once a doctor is
  // chosen. Ignored in edit mode where identity stays locked.
  tenantOptions = [],
  doctorOptions = [],
}) {
  const isDuplicate = mode === "duplicate";
  const initial = useMemo(() => deriveInitialForm(slots), [slots]);
  const [form, setForm] = useState(initial.form);
  // `touched` is sticky once a user interacts with a field — kept separate
  // from `mixed` (which is immutable for the lifetime of an opening) so the
  // change-detection logic can distinguish "user touched this Mixed field
  // and now wants to homogenize it" from "value just happens to equal initial".
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  // Snapshot the initial form values so `valueEqualsInitial` keeps working
  // even after the user has edited fields.
  const initialFormRef = useRef(initial.form);

  // Calendars are fetched lazily for the override doctor — only when the user
  // actually picks one in duplicate mode. Tracked with a request ref so a
  // mid-flight switch (user picks doctor A then immediately doctor B) ignores
  // the stale response. Declared up here so the re-seed effect below can
  // reset the list when the sheet re-opens.
  const [calendarOpts, setCalendarOpts] = useState([]);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const calendarsRequestRef = useRef(0);

  // Re-seed every time the sheet opens — the slot set may have shifted between
  // openings (different cell, refreshed data, ...).
  useEffect(() => {
    if (open) {
      setForm(initial.form);
      setTouched({});
      setErrors({});
      initialFormRef.current = initial.form;
      // The override calendar list is doctor-scoped; reset when the sheet
      // re-opens so a previous opening's list doesn't leak across.
      setCalendarOpts([]);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open || !isDuplicate || !form.doctorId) {
      setCalendarOpts([]);
      return;
    }
    const requestId = calendarsRequestRef.current + 1;
    calendarsRequestRef.current = requestId;
    setCalendarsLoading(true);
    fetchCalendars({ doctorId: form.doctorId })
      .then((res) => {
        if (calendarsRequestRef.current !== requestId) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        const opts = items
          .map((item) => {
            const value = String(item?.id || item?._id || "").trim();
            if (!value) return null;
            // Rule hints ("· non-blocking · local only") ride the label, like the slot form.
            const label = calendarOptionLabel(item) || value;
            return { value, label, raw: item };
          })
          .filter(Boolean);
        setCalendarOpts(opts);
        // Drop the override calendar id if it no longer matches the new
        // doctor's calendar list — prevents silently saving a copy against
        // a calendar that doesn't belong to the chosen doctor.
        setForm((prev) => {
          if (!prev.calendarId) return prev;
          const stillValid = opts.some((opt) => opt.value === prev.calendarId);
          return stillValid ? prev : { ...prev, calendarId: "" };
        });
      })
      .catch(() => {
        if (calendarsRequestRef.current !== requestId) return;
        setCalendarOpts([]);
      })
      .finally(() => {
        if (calendarsRequestRef.current === requestId) {
          setCalendarsLoading(false);
        }
      });
  }, [open, isDuplicate, form.doctorId]);

  const isRecurring = form.slotType === "recurring";
  const isMonthly = isRecurring && form.frequency === "monthly";
  const isMonthlyDayOfMonth = isMonthly && form.monthlyVariant === "dayOfMonth";
  const isMonthlyNthWeekday = isMonthly && form.monthlyVariant === "nthWeekday";

  // The "Mixed" badge is shown only while the field is still Mixed across
  // slots AND the user hasn't touched it yet. Once touched, applying will
  // homogenize the value so the Mixed state no longer applies.
  const isMixedDisplayed = (key) => Boolean(initial.mixed[key]) && !touched[key];

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const toggleArrayField = (key, value) => {
    setForm((prev) => {
      const list = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = list.includes(value);
      const next = exists ? list.filter((item) => item !== value) : [...list, value];
      return { ...prev, [key]: next };
    });
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const addDate = (date) => {
    const ymd = dateToYmd(date);
    if (!ymd) return;
    setForm((prev) => {
      if (prev.dates.includes(ymd)) return prev;
      return { ...prev, dates: [...prev.dates, ymd].sort() };
    });
    setTouched((prev) => (prev.dates ? prev : { ...prev, dates: true }));
  };

  const removeDate = (ymd) => {
    setForm((prev) => ({ ...prev, dates: prev.dates.filter((d) => d !== ymd) }));
    setTouched((prev) => (prev.dates ? prev : { ...prev, dates: true }));
  };

  const addExcludeDate = (date) => {
    const ymd = dateToYmd(date);
    if (!ymd) return;
    setForm((prev) => {
      if (prev.excludeDates.includes(ymd)) return prev;
      return { ...prev, excludeDates: [...prev.excludeDates, ymd].sort() };
    });
    setTouched((prev) => (prev.excludeDates ? prev : { ...prev, excludeDates: true }));
  };

  const removeExcludeDate = (ymd) => {
    setForm((prev) => ({ ...prev, excludeDates: prev.excludeDates.filter((d) => d !== ymd) }));
    setTouched((prev) => (prev.excludeDates ? prev : { ...prev, excludeDates: true }));
  };

  // Compute the set of fields that represent real changes versus the slot
  // set's starting state. A field "changed" when:
  //   • it was initially Mixed AND the user touched it → homogenizes the group
  //   • it was initially Shared AND the form value differs from initial
  const changedFields = useMemo(() => {
    const initialForm = initialFormRef.current;
    const result = {};
    for (const key of Object.keys(form)) {
      const wasMixed = Boolean(initial.mixed[key]);
      const initialValue = initialForm[key];
      const formValue = form[key];
      let changed;
      if (wasMixed) {
        changed = Boolean(touched[key]);
      } else if (Array.isArray(initialValue) || Array.isArray(formValue)) {
        changed = !arraysEqual(initialValue || [], formValue || []);
      } else {
        changed = initialValue !== formValue;
      }
      if (changed) result[key] = true;
    }
    return result;
  }, [form, touched, initial]);

  const changedKeys = Object.keys(changedFields);
  const hasChanges = changedKeys.length > 0;
  const changedLabels = changedKeys
    .map((key) => BULK_FIELD_LABELS[key] || key)
    .filter(Boolean);

  // Validation only runs on fields the user is actually changing. Fields the
  // user didn't touch get whatever the slot already has (per-slot fallback in
  // the parent), so requiring "≥1 weekday" doesn't make sense for an
  // untouched, otherwise-Mixed weekdays field.
  const validate = () => {
    const next = {};
    const c = changedFields;
    // Identity overrides — duplicate mode only. Calendars are doctor-scoped,
    // so a doctor change has to come with a calendar pick; without it the
    // source's calendar would belong to the wrong doctor and the server
    // would reject every copy.
    if (isDuplicate && c.doctorId && !form.calendarId) {
      next.calendarId = "Pick a calendar for the new doctor.";
    }
    if (c.startTime && !/^\d{2}:\d{2}$/.test(form.startTime)) {
      next.startTime = "Set a start time.";
    }
    if (c.endTime && !/^\d{2}:\d{2}$/.test(form.endTime)) {
      next.endTime = "Set an end time.";
    }
    if ((c.startTime || c.endTime) && form.startTime && form.endTime && form.endTime <= form.startTime) {
      next.endTime = "End must be after start.";
    }
    if (isRecurring && form.frequency === "weekly") {
      if (c.weekdays && (!Array.isArray(form.weekdays) || form.weekdays.length === 0)) {
        next.weekdays = "Pick at least one weekday.";
      }
      if (c.repeatIntervalWeeks) {
        const weeks = Number(form.repeatIntervalWeeks);
        if (!Number.isInteger(weeks) || weeks < 1) {
          next.repeatIntervalWeeks = "Repeat interval must be ≥ 1.";
        }
      }
    }
    if (isMonthly) {
      if (c.repeatIntervalMonths) {
        const months = Number(form.repeatIntervalMonths);
        if (!Number.isInteger(months) || months < 1) {
          next.repeatIntervalMonths = "Repeat interval must be ≥ 1.";
        }
      }
      if (isMonthlyDayOfMonth && c.monthDays) {
        if (!Array.isArray(form.monthDays) || form.monthDays.length === 0) {
          next.monthDays = "Pick at least one day of the month.";
        }
      }
      if (isMonthlyNthWeekday) {
        if (c.monthWeeks && (!Array.isArray(form.monthWeeks) || form.monthWeeks.length === 0)) {
          next.monthWeeks = "Pick at least one week of the month.";
        }
        if (c.weekdays && (!Array.isArray(form.weekdays) || form.weekdays.length === 0)) {
          next.weekdays = "Pick at least one weekday.";
        }
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleApply = () => {
    // Duplicate mode permits zero edits (the action itself — cloning — is the
    // change). Edit mode requires at least one touched field.
    if (!hasChanges && !isDuplicate) {
      toast.info("No changes to apply.");
      return;
    }
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    onApply?.(form, changedFields);
  };

  const slotCount = slots.length;
  const slotWord = slotCount === 1 ? "slot" : "slots";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="h-full max-h-dvh w-full gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="relative z-10 shrink-0 border-b border-border bg-background px-6 pb-4 pl-6 pr-14 pt-5 text-left">
          <SheetTitle className="flex items-center gap-2">
            {isDuplicate ? "Duplicate" : "Edit"} {slotCount} {slotWord}
            {busy ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
            ) : null}
          </SheetTitle>
          <SheetDescription>
            {isDuplicate
              ? "Create a copy of every visible slot in this group. Each copy keeps its anchor date — pick a different clinic or doctor below to re-target the copies, or leave them untouched to clone in place. Changing the doctor requires picking a calendar (calendars are doctor-scoped). Other touched fields are overridden on the copies; untouched fields are cloned as-is."
              : "Apply these settings to every visible slot in this group. Each slot keeps its doctor, tenant, calendar, and anchor date — only the fields below change."}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-full min-h-0 flex-1">
          <div className="space-y-5 px-6 py-5 pb-32">
            {isDuplicate ? (
              <>
                <OverrideComboField
                  label="Clinic (override)"
                  noun="clinic"
                  value={form.tenantId}
                  mixed={isMixedDisplayed("tenantId")}
                  options={tenantOptions}
                  onChange={(next) => setField("tenantId", next)}
                  placeholder="Keep each copy in its source clinic"
                />
                <OverrideComboField
                  label="Doctor (override)"
                  noun="doctor"
                  value={form.doctorId}
                  mixed={isMixedDisplayed("doctorId")}
                  options={doctorOptions}
                  onChange={(next) => {
                    setField("doctorId", next);
                    // Doctor change invalidates the override calendar — the
                    // calendar list refetches off the new doctor and any
                    // stale calendarId is cleared by the fetch effect above.
                    // Bump touched so the new (empty) calendar value flags
                    // the field as needing a fresh pick.
                    setField("calendarId", "");
                  }}
                  placeholder="Keep each copy with its source doctor"
                />
                {form.doctorId ? (
                  /* Calendar override only renders once a doctor is chosen —
                     the picker's options depend on the doctor and the field
                     is required whenever doctorId is in the changed set. */
                  <OverrideComboField
                    label="Calendar"
                    noun="calendar"
                    value={form.calendarId}
                    mixed={false}
                    options={calendarOpts}
                    onChange={(next) => setField("calendarId", next)}
                    placeholder={
                      calendarsLoading
                        ? "Loading calendars…"
                        : calendarOpts.length === 0
                          ? "No calendars for this doctor"
                          : "Pick a calendar for the new doctor"
                    }
                    loading={calendarsLoading}
                    disabled={calendarsLoading || calendarOpts.length === 0}
                    error={errors.calendarId}
                  />
                ) : null}
                <Separator />
              </>
            ) : null}

            <div className="space-y-2">
              <FieldLabel mixed={isMixedDisplayed("slotType")}>Type</FieldLabel>
              <div className="flex gap-2">
                <PillButton
                  active={!isRecurring}
                  onClick={() => setField("slotType", "static")}
                >
                  Static (one-off)
                </PillButton>
                <PillButton
                  active={isRecurring}
                  onClick={() => setField("slotType", "recurring")}
                >
                  Recurring
                </PillButton>
              </div>
            </div>

            {isDuplicate && !isRecurring ? (
              <div className="space-y-2">
                <FieldLabel mixed={isMixedDisplayed("date")}>Date</FieldLabel>
                <DatePicker
                  date={ymdToDate(form.date)}
                  setDate={(next) => setField("date", dateToYmd(next))}
                />
                <p className="text-xs text-muted-foreground/80">
                  The one-off date for every copy. Leave unchanged to keep each copy on its source date.
                </p>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <FieldLabel mixed={isMixedDisplayed("startTime")}>Start</FieldLabel>
                <Input
                  type="time"
                  value={form.startTime}
                  onChange={(event) => setField("startTime", event.target.value)}
                  className={cn("h-9", errors.startTime && "border-destructive")}
                />
                {errors.startTime ? (
                  <p className="text-xs text-destructive">{errors.startTime}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <FieldLabel mixed={isMixedDisplayed("endTime")}>End</FieldLabel>
                <Input
                  type="time"
                  value={form.endTime}
                  onChange={(event) => setField("endTime", event.target.value)}
                  className={cn("h-9", errors.endTime && "border-destructive")}
                />
                {errors.endTime ? (
                  <p className="text-xs text-destructive">{errors.endTime}</p>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel mixed={isMixedDisplayed("timezone")}>Timezone</FieldLabel>
              <Input
                list="bulk-edit-tz-list"
                value={form.timezone}
                onChange={(event) => setField("timezone", event.target.value.trim())}
                placeholder="UTC"
                className="h-9"
              />
              <datalist id="bulk-edit-tz-list">
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
            </div>

            {isRecurring ? (
              <>
                <Separator />
                <div className="space-y-2">
                  <FieldLabel mixed={isMixedDisplayed("frequency")}>Frequency</FieldLabel>
                  <div className="flex gap-2">
                    <PillButton
                      active={form.frequency === "weekly"}
                      onClick={() => setField("frequency", "weekly")}
                    >
                      Weekly
                    </PillButton>
                    <PillButton
                      active={form.frequency === "monthly"}
                      onClick={() => setField("frequency", "monthly")}
                    >
                      Monthly
                    </PillButton>
                  </div>
                </div>

                {form.frequency === "weekly" ? (
                  <>
                    <div className="space-y-2">
                      <FieldLabel mixed={isMixedDisplayed("weekdays")}>Weekdays</FieldLabel>
                      <div className="flex flex-wrap gap-1.5">
                        {WEEKDAY_LABELS.map((day) => (
                          <PillButton
                            key={day.iso}
                            active={form.weekdays.includes(day.iso)}
                            onClick={() => toggleArrayField("weekdays", day.iso)}
                            title={`ISO ${day.iso}`}
                          >
                            {day.short}
                          </PillButton>
                        ))}
                      </div>
                      {errors.weekdays ? (
                        <p className="text-xs text-destructive">{errors.weekdays}</p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <FieldLabel mixed={isMixedDisplayed("repeatIntervalWeeks")}>Repeat every</FieldLabel>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={52}
                          value={form.repeatIntervalWeeks}
                          onChange={(event) =>
                            setField(
                              "repeatIntervalWeeks",
                              Math.max(1, Number(event.target.value) || 1),
                            )
                          }
                          className="h-9 w-24"
                        />
                        <span className="text-sm text-muted-foreground">week(s)</span>
                      </div>
                      {errors.repeatIntervalWeeks ? (
                        <p className="text-xs text-destructive">{errors.repeatIntervalWeeks}</p>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <FieldLabel>Monthly pattern</FieldLabel>
                      <div className="flex gap-2">
                        <PillButton
                          active={form.monthlyVariant === "dayOfMonth"}
                          onClick={() => setField("monthlyVariant", "dayOfMonth")}
                          title="e.g. 1st and 15th of every month"
                        >
                          By day of month
                        </PillButton>
                        <PillButton
                          active={form.monthlyVariant === "nthWeekday"}
                          onClick={() => setField("monthlyVariant", "nthWeekday")}
                          title="e.g. 2nd Tuesday of every month"
                        >
                          By Nth week
                        </PillButton>
                      </div>
                    </div>

                    {form.monthlyVariant === "dayOfMonth" ? (
                      <div className="space-y-2">
                        <FieldLabel mixed={isMixedDisplayed("monthDays")}>Days of the month</FieldLabel>
                        <div className="flex flex-wrap gap-1.5">
                          {MONTH_DAY_VALUES.map((day) => (
                            <PillButton
                              key={day}
                              active={form.monthDays.includes(day)}
                              onClick={() => toggleArrayField("monthDays", day)}
                              title={`Day ${day}`}
                            >
                              {day}
                            </PillButton>
                          ))}
                          <PillButton
                            active={form.monthDays.includes(-1)}
                            onClick={() => toggleArrayField("monthDays", -1)}
                            title="Last day of the month (28/29/30/31)"
                          >
                            Last
                          </PillButton>
                        </div>
                        {errors.monthDays ? (
                          <p className="text-xs text-destructive">{errors.monthDays}</p>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <FieldLabel mixed={isMixedDisplayed("monthWeeks")}>Week of the month</FieldLabel>
                          <div className="flex flex-wrap gap-1.5">
                            {MONTH_WEEK_LABELS.map((week) => (
                              <PillButton
                                key={week.value}
                                active={form.monthWeeks.includes(week.value)}
                                onClick={() => toggleArrayField("monthWeeks", week.value)}
                                title={
                                  week.value === -1
                                    ? "Last occurrence of that weekday in the month"
                                    : `${week.short} occurrence in the month`
                                }
                              >
                                {week.short}
                              </PillButton>
                            ))}
                          </div>
                          {errors.monthWeeks ? (
                            <p className="text-xs text-destructive">{errors.monthWeeks}</p>
                          ) : null}
                        </div>
                        <div className="space-y-2">
                          <FieldLabel mixed={isMixedDisplayed("weekdays")}>Weekdays</FieldLabel>
                          <div className="flex flex-wrap gap-1.5">
                            {WEEKDAY_LABELS.map((day) => (
                              <PillButton
                                key={day.iso}
                                active={form.weekdays.includes(day.iso)}
                                onClick={() => toggleArrayField("weekdays", day.iso)}
                                title={`ISO ${day.iso}`}
                              >
                                {day.short}
                              </PillButton>
                            ))}
                          </div>
                          {errors.weekdays ? (
                            <p className="text-xs text-destructive">{errors.weekdays}</p>
                          ) : null}
                        </div>
                      </>
                    )}

                    <div className="space-y-2">
                      <FieldLabel mixed={isMixedDisplayed("repeatIntervalMonths")}>Repeat every</FieldLabel>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={24}
                          value={form.repeatIntervalMonths}
                          onChange={(event) =>
                            setField(
                              "repeatIntervalMonths",
                              Math.max(1, Number(event.target.value) || 1),
                            )
                          }
                          className="h-9 w-24"
                        />
                        <span className="text-sm text-muted-foreground">month(s)</span>
                      </div>
                      {errors.repeatIntervalMonths ? (
                        <p className="text-xs text-destructive">{errors.repeatIntervalMonths}</p>
                      ) : null}
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <FieldLabel mixed={isMixedDisplayed("dates")}>
                    Also include specific dates (optional)
                  </FieldLabel>
                  <p className="text-xs text-muted-foreground/80">
                    Extra one-off dates added on top of the {form.frequency === "monthly" ? "monthly" : "weekly"} pattern. Applies to every slot in the group; duplicates that already fall on the pattern are ignored.
                  </p>
                  <BulkDateListPicker
                    dates={form.dates}
                    onAdd={addDate}
                    onRemove={removeDate}
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel mixed={isMixedDisplayed("excludeDates")}>
                    Also exclude specific dates (optional)
                  </FieldLabel>
                  <p className="text-xs text-muted-foreground/80">
                    One-off dates removed from the {form.frequency === "monthly" ? "monthly" : "weekly"} pattern. Applies to every slot in the group; overrides any matching included or pattern date.
                  </p>
                  <BulkDateListPicker
                    dates={form.excludeDates}
                    onAdd={addExcludeDate}
                    onRemove={removeExcludeDate}
                  />
                </div>
              </>
            ) : null}

            <Separator />

            <div className="space-y-2">
              <FieldLabel mixed={isMixedDisplayed("subType")}>Visit subtype</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {SUB_TYPE_OPTIONS.map((opt) => (
                  <PillButton
                    key={opt.value || "any"}
                    active={form.subType === opt.value}
                    onClick={() => setField("subType", opt.value)}
                    title={opt.value ? `Tag every slot as ${opt.label.toLowerCase()}` : "No visit-type restriction"}
                  >
                    {opt.label}
                  </PillButton>
                ))}
              </div>
              {form.subType === "followUp" ? (
                <MoreTestsToggle
                  checked={form.moreTests === true}
                  mixed={isMixedDisplayed("moreTests")}
                  onToggle={(next) => setField("moreTests", next)}
                />
              ) : null}
            </div>

            <div className="space-y-2">
              <FieldLabel mixed={isMixedDisplayed("bookingMode")}>Booking mode</FieldLabel>
              <div className="flex gap-2">
                <PillButton
                  active={form.bookingMode === "auto"}
                  onClick={() => setField("bookingMode", "auto")}
                  title="Patient self-serve"
                >
                  Auto
                </PillButton>
                <PillButton
                  active={form.bookingMode === "manual"}
                  onClick={() => setField("bookingMode", "manual")}
                  title="Reception-only"
                >
                  Manual
                </PillButton>
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel mixed={isMixedDisplayed("placeholder")}>Label</FieldLabel>
              <Input
                value={form.placeholder}
                onChange={(event) => setField("placeholder", event.target.value)}
                placeholder="e.g. Morning clinic"
                className="h-9"
                maxLength={120}
              />
            </div>

            <div className="space-y-2">
              <FieldLabel mixed={isMixedDisplayed("colorHex")}>Color</FieldLabel>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.colorHex || "#0ea5e9"}
                  onChange={(event) => setField("colorHex", event.target.value)}
                  className="h-9 w-12 cursor-pointer rounded-md border border-border bg-transparent p-1"
                  aria-label="Slot color"
                />
                <Input
                  value={form.colorHex}
                  onChange={(event) => setField("colorHex", event.target.value.trim())}
                  placeholder="#0ea5e9"
                  className="h-9 w-32 font-mono uppercase"
                  maxLength={7}
                />
                {form.colorHex ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2 text-xs"
                    onClick={() => setField("colorHex", "")}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="shrink-0 space-y-3 border-t border-border bg-background px-6 py-4">
          {/* Live "diff" summary — names the fields that will actually be
              updated. Updates as the user edits the form. */}
          <div
            className={cn(
              "rounded-md border px-3 py-2 text-[11px]",
              hasChanges || isDuplicate
                ? "border-primary/40 bg-primary/5 text-foreground"
                : "border-border bg-muted/30 text-muted-foreground",
            )}
            aria-live="polite"
          >
            {isDuplicate ? (
              hasChanges ? (
                <>
                  <span className="font-semibold">
                    Will copy {slotCount} {slotWord} and override{" "}
                  </span>
                  <span>{changedLabels.join(", ")} </span>
                  <span className="text-muted-foreground">
                    on the copies. Untouched fields are cloned as-is.
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold">
                    Will copy {slotCount} {slotWord} as exact duplicates.{" "}
                  </span>
                  <span className="text-muted-foreground">
                    Edit any field above to override it on the copies.
                  </span>
                </>
              )
            ) : hasChanges ? (
              <>
                <span className="font-semibold">Will update </span>
                <span>{changedLabels.join(", ")} </span>
                <span className="text-muted-foreground">
                  on {slotCount} {slotWord}. Fields you haven't touched stay as-is.
                </span>
              </>
            ) : (
              <span>
                No changes yet. Edit a field above to bulk-update {slotCount}{" "}
                {slotWord}.
              </span>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              disabled={
                busy || slotCount === 0 || (!hasChanges && !isDuplicate)
              }
              aria-busy={busy}
            >
              {busy ? <Spinner className="mr-2 size-4" /> : null}
              {isDuplicate
                ? `Create ${slotCount} ${slotCount === 1 ? "copy" : "copies"}`
                : `Apply to ${slotCount} ${slotWord}`}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
